import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import AnimatedButton from '../common/AnimatedButton';
import AnimatedSection from '../common/AnimatedSection';
import { FaRocket, FaComments, FaChevronRight } from 'react-icons/fa';

const CtaSection = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.2
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.6, 0.05, 0.01, 0.9]
      }
    }
  };

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background with gradient and pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-blue-700 to-blue-900 z-0">
        {/* Diagonal pattern overlay */}
        <div className="absolute inset-0 opacity-10" 
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>
      </div>

      {/* Animated elements */}
      <div className="absolute inset-0 overflow-hidden z-0">
        {/* Floating circles */}
        <motion.div 
          className="absolute top-0 right-10 w-96 h-96 rounded-full bg-white opacity-10 blur-xl"
          animate={{ 
            scale: [1, 1.2, 1],
            x: [0, 30, 0],
            y: [0, -30, 0],
          }}
          transition={{ 
            duration: 15,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        <motion.div 
          className="absolute bottom-0 left-10 w-80 h-80 rounded-full bg-accent opacity-20 blur-xl"
          animate={{ 
            scale: [1, 1.3, 1],
            x: [0, -30, 0],
            y: [0, 30, 0],
          }}
          transition={{ 
            duration: 18,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        
        {/* Animated rocket in the background */}
        <motion.div
          className="absolute -right-20 top-1/2 transform -translate-y-1/2 text-white opacity-5 text-[300px]"
          animate={{
            y: [-20, 20, -20],
            rotate: [0, 5, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <FaRocket />
        </motion.div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="flex flex-col lg:flex-row items-center justify-between gap-12 py-8"
        >
          {/* Left side content */}
          <motion.div variants={itemVariants} className="lg:w-1/2">
            <motion.div 
              className="inline-block bg-accent text-white font-bold px-4 py-2 rounded-full mb-6"
              whileHover={{ scale: 1.05, backgroundColor: '#ffc107' }}
              whileTap={{ scale: 0.95 }}
            >
              Boostez votre présence en ligne
            </motion.div>
            
            <motion.h2 
              className="text-4xl md:text-5xl font-bold mb-6 text-white leading-tight"
              variants={itemVariants}
            >
              Prêt à transformer votre <span className="text-accent">présence digitale</span> ?
            </motion.h2>
            
            <motion.p 
              className="text-xl text-blue-100 mb-8"
              variants={itemVariants}
            >
              Demandez dès maintenant votre Expertise gratuite et découvrez comment améliorer votre site web pour attirer plus de clients et augmenter vos conversions.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4"
              variants={itemVariants}
            >
              <AnimatedButton 
                to="/Expertise" 
                type="secondary"
                size="lg"
                className="px-8 py-4 text-lg font-bold shadow-lg"
              >
                <div className="flex items-center justify-center">
                  <FaRocket className="mr-2" />
                  Expertise gratuite
                </div>
              </AnimatedButton>
              
              <AnimatedButton 
                to="/contact" 
                type="outline"
                size="lg"
                className="border-white text-white hover:bg-white hover:text-primary px-8 py-4 text-lg font-bold group"
              >
                <div className="flex items-center justify-center">
                  <FaComments className="mr-2 group-hover:text-primary" />
                  <span className="group-hover:text-primary">Nous contacter</span>
                </div>
              </AnimatedButton>
            </motion.div>
          </motion.div>
          
          {/* Right side card */}
          <motion.div 
            variants={itemVariants} 
            className="lg:w-5/12 bg-white rounded-2xl shadow-2xl overflow-hidden"
            whileHover={{ y: -10, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}
          >
            <div className="bg-gradient-to-r from-accent to-yellow-500 p-4 text-white font-bold text-xl">
              Ce que vous obtiendrez
            </div>
            <div className="p-8">
              <ul className="space-y-4">
                {[
                  'Analyse complète de votre site web',
                  'Recommandations d\'optimisation SEO',
                  'Expertise de performance et vitesse',
                  'Analyse de l\'expérience utilisateur',
                  'Stratégie d\'amélioration personnalisée'
                ].map((item, index) => (
                  <motion.li 
                    key={index}
                    className="flex items-start"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + (index * 0.1) }}
                  >
                    <span className="text-accent mr-2 mt-1"><FaChevronRight /></span>
                    <span className="text-gray-700">{item}</span>
                  </motion.li>
                ))}
              </ul>
              
              <motion.div 
                className="mt-8 text-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <AnimatedButton 
                  to="/Expertise" 
                  type="primary"
                  size="lg"
                  className="w-full"
                >
                  Demander mon Expertise maintenant
                </AnimatedButton>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Top wave divider */}
      <div className="absolute top-0 left-0 right-0 h-16 bg-white" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 0)' }}></div>
    </section>
  );
};

export default CtaSection;